import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ai } from '@repletoryreach/sdk-api';
import { Card, Stack, Text, Title, Button, Group } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { formatCount } from '../../lib/format';
import { useT } from '../../i18n/I18nProvider';
import { AiError, ModelSelect, UnitCost, readAsDataUrl, useModelsOf } from './shared';

const MAX_BYTES = 20 * 1024 * 1024;
const ACCEPT = 'audio/*,.mp3,.wav,.ogg,.opus,.oga,.flac,.m4a';
const LANGUAGES = ['', 'en-ZA', 'af-ZA', 'zu-ZA', 'en-GB', 'en-US'] as const;

/**
 * Speech to text (14 October 2026): a recording turned into text, charged per minute by the second. A short one
 * answers at once; a longer one keeps running, and the page asks for it every few seconds until it is done.
 */
export function TranscribeTab() {
  const t = useT();
  const models = useModelsOf('transcribe');
  const [model, setModel] = useState('');
  const chosen = models.find((m) => m.code === model) ?? models[0];
  const [file, setFile] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [language, setLanguage] = useState<(typeof LANGUAGES)[number]>('');

  const run = useMutation({
    mutationFn: async () => {
      const data = await readAsDataUrl(file!);
      return ai.transcribe({ audio: { data, media_type: file!.type || null }, language: language || null, model: chosen?.code ?? null });
    }
  });

  const started = run.data?.data;
  const running = started?.status === 'running';
  const poll = useQuery({
    queryKey: ['ai', 'request', started?.request_id],
    queryFn: () => ai.getRequest(started!.request_id),
    enabled: running,
    refetchInterval: (q) => (q.state.data?.data.status === 'held' ? 5000 : false)
  });
  const later = poll.data?.data;
  const text = running ? (later?.status === 'done' ? later.result ?? '' : null) : started?.text ?? null;
  const failed = running && later?.status === 'failed';
  const charged = running ? later?.charged_minor ?? 0 : started?.charged_minor ?? 0;

  function choose(f: File | undefined) {
    setProblem(null);
    setFile(null);
    if (!f) return;
    if (f.size > MAX_BYTES) { setProblem(t('ai.transcribe.tooBig')); return; }
    setFile(f);
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(text ?? '');
      notifySuccess(t('ai.result.copied'), t('ai.title'));
    } catch { /* the browser refused: the text stays on the page to select */ }
  }

  return (
    <Stack gap="lg">
      <Card withBorder>
        <form onSubmit={(e) => { e.preventDefault(); if (file && chosen) run.mutate(); }}>
          <Stack gap="md">
            <Title order={4}>{t('ai.transcribe.title')}</Title>
            <Stack gap={4}>
              <label className="rr-ai__file">
                <Text role="inputLabel">{t('ai.transcribe.recording')}</Text>
                <input type="file" accept={ACCEPT} data-testid="ai-transcribe-file" onChange={(e) => choose(e.currentTarget.files?.[0])} />
              </label>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('ai.transcribe.recordingHint')}</Text>
            </Stack>
            {problem && <Alert color="warning">{problem}</Alert>}
            <Select
              label={t('ai.transcribe.language')}
              value={language}
              onChange={(e) => setLanguage(e.currentTarget.value as (typeof LANGUAGES)[number])}
              data={LANGUAGES.map((l) => ({ value: l, label: t(`ai.transcribe.language.${l || 'auto'}`) }))}
            />
            <ModelSelect kind="transcribe" value={chosen?.code ?? ''} onChange={setModel} />
            <Group justify="flex-end">
              <Button type="submit" loading={run.isPending} disabled={!file || !chosen} data-testid="ai-transcribe-run">{t('ai.transcribe.run')}</Button>
            </Group>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('ai.transcribe.chargeNote')}</Text>
          </Stack>
        </form>
      </Card>
      <AiError error={run.error} />
      {started && (
        <Card withBorder>
          <Stack gap="xs" data-testid="ai-result">
            {started.stub && <Text role="bodySmall">{t('ai.result.stub')}</Text>}
            {text === null && !failed && <Alert color="info">{t('ai.transcribe.running')}</Alert>}
            {failed && <Alert color="error" role="alert">{later?.error ?? t('ai.transcribe.failed')}</Alert>}
            {text !== null && (
              <div className="rr-ai__output rr-ai__transcript" data-testid="ai-transcript">
                {text || t('ai.transcribe.silent')}
              </div>
            )}
            {text !== null && (
              <UnitCost minor={charged}>
                <Button size="xs" variant="subtle" onClick={copy}>{t('ai.result.copy')}</Button>
              </UnitCost>
            )}
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('ai.transcribe.length', { seconds: formatCount(Math.round(started.seconds)) })}
            </Text>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
